// /src/ui/SceneView.tsx

import React, { useEffect, useMemo, useRef } from 'react';
import { SceneManager } from '@/engine/SceneManager';
import { MAIN_SUBMODEL_ID, useModelStore } from '@/store/modelStore';
import { setSceneManager } from '@/ui/sceneBridge';
import { useThemeStore } from '@/store/themeStore';

function makeMatrix(x: number, y: number, z: number): number[] {
  return [
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    x, y, z, 1,
  ];
}

export default function SceneView() {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const managerRef = useRef<SceneManager | null>(null);
  const theme = useThemeStore((s) => s.theme);

  const parts = useModelStore((s) => s.parts);
  const instances = useModelStore((s) => s.instances);
  const selectedId = useModelStore((s) => s.selectedId);
  const renderMode = useModelStore((s) => s.renderMode);
  const uiMode = useModelStore((s) => s.uiMode);
  const pendingPlacement = useModelStore((s) => s.pendingPlacement);
  const viewStep = useModelStore((s) => s.viewStep);
  const activeSubmodelId = useModelStore((s) => s.activeSubmodelId);
  const setSelected = useModelStore((s) => s.setSelected);
  const updatePartMatrix = useModelStore((s) => s.updatePartMatrix);
  const updatePartProps = useModelStore((s) => s.updatePartProps);
  const updateInstanceMatrix = useModelStore((s) => s.updateInstanceMatrix);
  const updateInstanceProps = useModelStore((s) => s.updateInstanceProps);
  const setCollision = useModelStore((s) => s.setCollision);

  const callbacks = useMemo(() => {
    return {
      onSelect: (id: string | null) => setSelected(id),
      onMatrixChange: (id: string, m: number[], commit: boolean) => {
        // During drag we don't push history; on commit we do.
        const st = useModelStore.getState();
        const isInstance = st.instances.some((i) => i.id === id);
        if (isInstance) {
          if (commit) updateInstanceProps(id, { matrix: m } as any);
          else updateInstanceMatrix(id, m);
        } else {
          if (commit) updatePartProps(id, { matrix: m } as any);
          else updatePartMatrix(id, m);
        }
      },
      getMode: () => useModelStore.getState().mode,
      getSnap: () => useModelStore.getState().snap,
      getUiMode: () => useModelStore.getState().uiMode,
      onCollision: (c: 'ok' | 'warn' | 'bad') => setCollision(c),
      getSubmodelLabel: (submodelId: string) => {
        const st = useModelStore.getState();
        return st.submodels.find((sm) => sm.id === submodelId)?.name ?? submodelId;
      },
      onPlacementCommit: (point: { x: number; y: number; z: number }) => {
        const st = useModelStore.getState();
        const pending = st.pendingPlacement;
        if (!pending || st.uiMode !== 'edit') return;
        const targetSubmodel = st.submodels.some((sm) => sm.id === pending.submodelId)
          ? pending.submodelId
          : MAIN_SUBMODEL_ID;

        st.addPart({
          name: pending.name,
          partFile: pending.partFile,
          ldrawColor: pending.ldrawColor,
          colorHex: pending.colorHex,
          step: pending.step,
          submodelId: targetSubmodel,
          matrix: makeMatrix(point.x, point.y, point.z),
        });
        st.pushToast({
          title: 'Added part',
          detail: `${pending.name} (${pending.partFile})`,
          kind: 'ok',
        });
        st.setPendingPlacement(null);
      },
    };
  }, [setSelected, updatePartMatrix, updatePartProps, updateInstanceMatrix, updateInstanceProps, setCollision]);

  useEffect(() => {
    if (!hostRef.current) return;
    const m = new SceneManager(hostRef.current, callbacks);
    // Apply theme before the first render frame.
    m.setTheme(theme);
    m.setPlacementDraft(useModelStore.getState().pendingPlacement);
    managerRef.current = m;
    setSceneManager(m);
    return () => {
      managerRef.current?.dispose();
      managerRef.current = null;
      setSceneManager(null);
    };
  }, [callbacks]);

  useEffect(() => {
    managerRef.current?.setTheme(theme);
  }, [theme]);

  useEffect(() => {
    managerRef.current?.syncParts(parts, instances);
  }, [parts, instances]);

  useEffect(() => {
    managerRef.current?.setSelected(selectedId);
  }, [selectedId]);

  useEffect(() => {
    managerRef.current?.setRenderMode(renderMode);
  }, [renderMode]);

  useEffect(() => {
    managerRef.current?.setUiMode(uiMode);
  }, [uiMode]);

  useEffect(() => {
    managerRef.current?.setPlacementDraft(pendingPlacement);
  }, [pendingPlacement]);

  useEffect(() => {
    managerRef.current?.setSubmodelFilter(activeSubmodelId);
  }, [activeSubmodelId]);

  useEffect(() => {
    managerRef.current?.setViewStep(viewStep);
  }, [viewStep]);

  return <div ref={hostRef} className="canvasHost" />;
}
